import React from 'react';
import { Checkbox } from '../../components/Checkbox';
import { ListItemIcon } from '../../components/ListItemIcon';
import { ListItemText } from '../../components/ListItemText';
import { MenuItem } from '../../components/MenuItem';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import { useSelectContext } from '../Select/Context';

export interface IconOverride {
  /**
   * The name of the icon to use.
   */
  name: React.ComponentProps<typeof ListItemIcon>['name'];
  /**
   * Overrides the Icon's color.
   */
  color: React.ComponentProps<typeof ListItemIcon>['color'];
  /**
   * Overrides the Icon's size.
   */
  size: React.ComponentProps<typeof ListItemIcon>['size'];
}

interface OptionProps {
  /**
   * The label - only supports a string.
   * */
  children: React.ComponentProps<typeof ListItemText>['text'];
  subtext?: React.ComponentProps<typeof ListItemText>['subtext'];
  testId?: React.ComponentProps<typeof MenuItem>['testId'];

  /**
   * If true, the Option will have some ui affordances indicating that it is selected.
   */
  selected?: boolean;

  /**
   * An icon that will appear to the left of the option label. It will be
   * replaced by a selected icon if the option is selected.
   */
  leftIcon?: React.ComponentProps<typeof ListItemIcon>['name'] | IconOverride;

  /**
   * If provided, this will render to the right of the option's text. Can be used, for example,
   * to add a tooltip to the option.
   */
  rightAdornment?: React.ReactNode;

  /**
   * If provided, this will render to the left of the option's text, but to the right of the
   * checkbox.
   */
  leftAdornment?: React.ReactNode;

  /**
   * If true, the Option will have some ui affordances indicating that it is
   * highlighted.
   *
   * _Note_: Highlighted should be used when you need to indicate that the user is
   * maybe going to interact with the option. On hover or when the item is "highlighted"
   * from keyboard selection logic. It's different than `selected` because it's an intermediary state
   * between not-interacted-with and selected.
   */
  highlighted?: React.ComponentProps<typeof MenuItem>['highlighted'];

  /**
   * If true, the Option will have some ui affordances indicating that it is
   * disabled. This will also prevent the onClick function from firing.
   */
  disabled?: React.ComponentProps<typeof MenuItem>['disabled'];

  /**
   * If true, the Option will have a smaller font size to support higher density list
   */
  compact?: boolean;

  /**
   * If provided, the Option will have a checkbox rendered to the left of the option label with the provided color.
   * Otherwise, the activated color will be used.
   */
  checkColor?: React.ComponentProps<typeof ListItemIcon>['color'];

  /**
   * If true, the Option will not have inset applied to it when nothing should be rendered to the left of the option.
   */
  disableInset?: boolean;

  onMouseMove?: React.ComponentProps<typeof MenuItem>['onMouseMove'];
  onMouseEnter?: React.ComponentProps<typeof MenuItem>['onMouseEnter'];
  onMouseLeave?: React.ComponentProps<typeof MenuItem>['onMouseLeave'];
  onClick: React.ComponentProps<typeof MenuItem>['onClick'];
}

// eslint-disable-next-line react/display-name
export const Option = React.forwardRef<HTMLLIElement, OptionProps>(
  (
    {
      children,
      leftIcon,
      leftAdornment,
      rightAdornment,
      selected = false,
      disabled = false,
      compact = false,
      highlighted,
      onMouseMove,
      onMouseEnter,
      onMouseLeave,
      onClick,
      subtext,
      testId = 'option',
      checkColor = 'activated',
      disableInset = false,
    },
    ref,
  ) => {
    const getIconProps = (
      icon: React.ComponentProps<typeof ListItemIcon>['name'] | IconOverride,
    ): IconOverride =>
      typeof icon === 'string'
        ? {
            name: icon,
            color: 'default',
            size: 16,
          }
        : {
            name: icon.name,
            color: icon.color,
            size: icon.size,
          };
    const { multiple } = useSelectContext();
    return (
      <MenuItem
        data-component="Option"
        ref={ref}
        role="option"
        testId={testId}
        disabled={disabled}
        highlighted={highlighted}
        onMouseMove={onMouseMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        tabIndex={-1}
        {...identifyComponent('option')}
      >
        {!multiple && selected && (
          <ListItemIcon
            name="checkmark"
            color={checkColor}
            size={leftIcon ? getIconProps(leftIcon).size : 16}
          />
        )}
        {!multiple && !selected && leftIcon && <ListItemIcon {...getIconProps(leftIcon)} />}
        {multiple && <Checkbox checked={selected} onChange={() => null} />}
        <Box display="flex" flexDirection="row" alignItems="center" flex={1} minWidth={0}>
          {leftAdornment && <Box marginRight={8}>{leftAdornment}</Box>}
          <ListItemText
            noWrap
            inset={!selected && !leftIcon && !leftAdornment && !multiple && !disableInset}
            text={children}
            subtext={subtext}
            compact={compact}
          />
        </Box>
        {rightAdornment}
      </MenuItem>
    );
  },
);
